import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FileText, Newspaper, Video, Inbox, ListTree, Calendar, Users, BarChart3, Megaphone,
  Image as ImageIcon, Briefcase, UserRound, Settings, Mail, Phone, CalendarPlus,
  CheckCircle2, Clock, ChevronRight, ExternalLink, UserPlus, X, Save,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageTitle } from "./Layout";
import { useAdmin } from "./AdminApp";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Field } from "../../components/admin/Fields";

const QUICK_LINKS = [
  { to: "sayfalar", label: "Sayfalar", icon: FileText, hint: "İçerik düzenle" },
  { to: "haberler", label: "Yazılar", icon: Newspaper, hint: "Blog & haberler" },
  { to: "videolar", label: "Video Galerisi", icon: Video, hint: "Albümler" },
  { to: "galeri", label: "Resim Galerisi", icon: ImageIcon, hint: "Fotoğraflar" },
  { to: "calismalarim", label: "Çalışmalarım", icon: Briefcase, hint: "Portfolyo" },
  { to: "profil", label: "Profil & Kampanya", icon: UserRound, hint: "Hakkımda" },
  { to: "takvim", label: "Takvim", icon: Calendar, hint: "Etkinlikler" },
  { to: "workspace/talepler", label: "Aksiyon Takibi", icon: CalendarPlus, hint: "Randevular" },
  { to: "rehber", label: "Rehber", icon: Users, hint: "Kontaklar" },
  { to: "reklam", label: "Reklam", icon: Megaphone, hint: "Promo modal" },
  { to: "istatistik", label: "İstatistikler", icon: BarChart3, hint: "Ziyaretçi" },
  { to: "mesajlar", label: "Mesajlar", icon: Inbox, hint: "Gelen kutusu" },
  { to: "menu", label: "Menü", icon: ListTree, hint: "Site menüsü" },
  { to: "ayarlar", label: "Ayarlar", icon: Settings, hint: "Yapılandırma" },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function QuickAddContactModal({ open, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.name.trim() && !form.email.trim() && !form.phone.trim()) {
      return toast.error("En az ad, e-posta veya telefon girin");
    }
    setBusy(true);
    try {
      await api.post("/admin/contacts", form);
      toast.success("Rehbere eklendi");
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
      setForm({ name: "", email: "", phone: "", note: "" });
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="quick-contact-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-serif text-xl font-bold">Rehbere Hızlı Ekle</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-900" data-testid="qc-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Ad Soyad">
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="qc-name" autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="E-posta">
              <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="qc-email" />
            </Field>
            <Field label="Telefon">
              <Input type="tel" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+90..." data-testid="qc-phone" />
            </Field>
          </div>
          <Field label="Not">
            <Textarea rows={2} value={form.note} onChange={(e) => upd("note", e.target.value)} data-testid="qc-note" />
          </Field>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>Vazgeç</Button>
            <Button onClick={submit} disabled={busy} data-testid="qc-submit"><Save size={14} className="mr-1" /> Kaydet</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, Icon, to, accent }) {
  return (
    <Link to={`/admin/${to}`} className="group bg-white border rounded-xl p-4 sm:p-5 hover:shadow-md transition-all hover:-translate-y-0.5" data-testid={`dash-stat-${to}`}>
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon size={17} />
        </div>
        <ChevronRight size={14} className="text-stone-300 group-hover:text-stone-500 transition-colors" />
      </div>
      <p className="text-3xl font-bold mt-3 tabular-nums">{value ?? "–"}</p>
      <p className="text-sm text-stone-500 mt-0.5">{label}</p>
    </Link>
  );
}

export default function Dashboard() {
  const { settings } = useAdmin();
  const [quickContact, setQuickContact] = useState(false);
  const { data } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => api.get("/admin/stats").then((r) => r.data),
    refetchInterval: 30000,
  });
  const { data: upcoming = [] } = useQuery({
    queryKey: ["admin", "events-upcoming"],
    queryFn: () => api.get("/admin/calendar/upcoming").then((r) => r.data),
    refetchInterval: 60000,
  });
  const { data: appts = [] } = useQuery({
    queryKey: ["admin", "appts-dashboard"],
    queryFn: () => api.get("/admin/appointments?status=awaiting_approval").then((r) => r.data),
    refetchInterval: 30000,
  });
  const { data: callMe = [] } = useQuery({
    queryKey: ["admin", "callme-dashboard"],
    queryFn: () => api.get("/admin/appointments?status=call_me").then((r) => r.data),
    refetchInterval: 30000,
  });

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 6) return "İyi geceler";
    if (h < 12) return "Günaydın";
    if (h < 18) return "İyi günler";
    return "İyi akşamlar";
  })();
  const name = settings?.profile?.name?.tr || settings?.profile?.name || "";

  return (
    <>
      <PageTitle
        title={`${greeting}${name ? ", " + String(name).split(" ")[0] : ""} 👋`}
        sub={`Aktif tema: ${settings.theme?.mode === "adaylik" ? "Adaylık" : "Normal"} · Sitenin kalbi burada.`}
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setQuickContact(true)} data-testid="dash-quick-contact">
              <UserPlus size={14} className="mr-1" /> Rehbere Ekle
            </Button>
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-amber-700 font-semibold" data-testid="dash-view-site">
              <ExternalLink size={14} /> Siteyi gör
            </a>
          </div>
        }
      />

      {/* Priority stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <StatCard label="Okunmamış mesaj" value={data?.unread} Icon={Inbox} to="mesajlar" accent="bg-amber-50 text-amber-700" />
        <StatCard label="Onay bekleyen talep" value={data?.appt_awaiting} Icon={CalendarPlus} to="talepler" accent="bg-blue-50 text-blue-700" />
        <StatCard label="Geri arama talebi" value={data?.appt_call_me} Icon={Phone} to="talepler" accent="bg-violet-50 text-violet-700" />
        <StatCard label="Yaklaşan etkinlik" value={data?.events_upcoming} Icon={Calendar} to="takvim" accent="bg-emerald-50 text-emerald-700" />
        <StatCard label="Rehber kaydı" value={data?.contacts} Icon={Users} to="rehber" accent="bg-rose-50 text-rose-700" />
        <StatCard label="Sayfa" value={data?.pages} Icon={FileText} to="sayfalar" accent="bg-stone-100 text-stone-700" />
      </div>

      {/* Lively activity row */}
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        {/* Pending appointments */}
        <div className="bg-white border rounded-xl p-5" data-testid="dash-pending-appts">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg font-bold flex items-center gap-2">
              <CalendarPlus size={16} className="text-blue-700" /> Onay Bekleyen Talepler
            </h3>
            <Link to="/admin/workspace/talepler" className="text-xs text-stone-500 hover:text-amber-700 font-semibold">Tümü →</Link>
          </div>
          {appts.length === 0 && callMe.length === 0 ? (
            <p className="text-sm text-stone-400 py-4 text-center">Harika — tüm talepler ele alınmış.</p>
          ) : (
            <div className="space-y-2">
              {appts.slice(0, 3).map((a) => (
                <Link key={a.id} to="/admin/workspace/talepler" className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-blue-50/50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{a.name}</div>
                    {a.title && <div className="text-xs text-amber-700 truncate">{a.title}</div>}
                    <div className="text-[11px] text-stone-500 flex items-center gap-2 mt-0.5">
                      <Clock size={10} /> {a.proposed_slots?.length || 0} tarih önerisi
                    </div>
                  </div>
                  <span className="text-[10px] font-bold rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 whitespace-nowrap">Onay bekliyor</span>
                </Link>
              ))}
              {callMe.slice(0, 2).map((a) => (
                <Link key={a.id} to="/admin/workspace/talepler" className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-violet-50/50 transition-colors">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{a.call_me_name || a.name}</div>
                    <div className="text-xs text-violet-700 truncate flex items-center gap-1"><Phone size={10} />{a.call_me_phone}</div>
                  </div>
                  <span className="text-[10px] font-bold rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 whitespace-nowrap">Arama bekliyor</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming events */}
        <div className="bg-white border rounded-xl p-5" data-testid="dash-upcoming">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg font-bold flex items-center gap-2">
              <Calendar size={16} className="text-emerald-700" /> Yaklaşan Etkinlikler
            </h3>
            <Link to="/admin/workspace/takvim" className="text-xs text-stone-500 hover:text-amber-700 font-semibold">Takvim →</Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-stone-400 py-4 text-center">Önümüzdeki günlerde planlı etkinlik yok.</p>
          ) : (
            <div className="space-y-2">
              {upcoming.slice(0, 5).map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-emerald-50/40 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{e.title}</div>
                    <div className="text-[11px] text-stone-500 mt-0.5">{fmtDate(e.start_at)} {e.location ? "· " + e.location : ""}</div>
                  </div>
                  {e.source === "appointment" && (
                    <span className="text-[10px] font-bold rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">Randevu</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Shortcut grid */}
      <div className="mt-8">
        <h2 className="font-semibold text-stone-500 text-xs uppercase tracking-wider mb-3">Kısa Yollar</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="dash-shortcuts">
          {QUICK_LINKS.map(({ to, label, icon: Icon, hint }) => (
            <Link
              key={to}
              to={`/admin/${to}`}
              data-testid={`dash-link-${to}`}
              className="group flex flex-col items-center gap-2 p-3.5 bg-white border rounded-xl text-center hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-stone-50 group-hover:bg-amber-50 flex items-center justify-center text-stone-500 group-hover:text-amber-700 transition-colors">
                <Icon size={18} />
              </div>
              <div className="text-sm font-semibold leading-tight">{label}</div>
              <div className="text-[11px] text-stone-400">{hint}</div>
            </Link>
          ))}
        </div>
      </div>

      {/* Audit log */}
      <h2 className="font-semibold mt-10 mb-3 text-stone-500 text-xs uppercase tracking-wider">Son İşlemler</h2>
      <div className="bg-white border rounded-xl divide-y" data-testid="audit-log">
        {(data?.audit || []).slice(0, 8).map((a, i) => (
          <div key={a.id || i} className="px-5 py-2.5 text-sm flex justify-between gap-4 items-center">
            <span className="font-mono text-xs text-stone-700 truncate">{a.action}</span>
            <span className="text-stone-400 text-xs whitespace-nowrap">{new Date(a.at).toLocaleString("tr-TR")}</span>
          </div>
        ))}
        {!data?.audit?.length && <p className="px-5 py-6 text-sm text-stone-400">Henüz işlem yok.</p>}
      </div>
      <QuickAddContactModal open={quickContact} onClose={() => setQuickContact(false)} />
    </>
  );
}
